import api from "./api";
import * as actionTypes from "./actionTypes";
import { sendHttpRequest, sendHttpCommandAndWaitForEvent } from "actions/actionCreators/httpActions";
import { showError } from "actions/actionCreators/snackbarActions";
import * as eventTypes from "./eventTypes";

export function loadSchemaExchangeRates(documentId, siteId) {
  return async (dispatch, getState) => {
    const { schemaExchangeRatesLoaded } = getState().currentOffer.feesData;
    if (schemaExchangeRatesLoaded) {
      return;
    }

    dispatch({ type: actionTypes.LOAD_SCHEMA_EXCHANGE_RATE_STARTED, payload: { documentId, siteId } });
    try {
      let schemaExchangeRates = await dispatch(sendHttpRequest(api.getSchemaExchangeRates(documentId, siteId)));
      dispatch({ type: actionTypes.LOAD_SCHEMA_EXCHANGE_RATE_SUCCESS, payload: { schemaExchangeRates } });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_SCHEMA_EXCHANGE_RATE_FAILED, payload: { documentId, siteId, error } });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadOfferFees(documentId, siteId) {
  return async (dispatch, getState) => {
    const { feesLoaded } = getState().currentOffer.feesData;
    if (feesLoaded) {
      return;
    }

    dispatch({ type: actionTypes.LOAD_OFFER_FEES_STARTED, payload: { documentId, siteId } });
    try {
      let fees = await dispatch(sendHttpRequest(api.getFees(documentId, siteId)));
      dispatch({ type: actionTypes.LOAD_OFFER_FEES_SUCCESS, payload: { fees } });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_OFFER_FEES_FAILED, payload: { documentId, siteId, error } });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function updateOfferFeeCatalog(documentId, siteId, fees) {
  return async dispatch => {
    dispatch({ type: actionTypes.UPDATE_OFFER_FEES_STARTED, payload: { documentId, siteId, fees } });
    dispatch(
      sendHttpCommandAndWaitForEvent(
        api.updateOfferFeeCatalog(documentId, siteId, fees),
        "UpdateOfferFeeCatalogCommand",
        [eventTypes.OFFER_FINANCIAL_FEES_CATALOG_UPDATED]
      )
    )
      .then(event => {
        if (!event.ok) {
          dispatch({
            type: actionTypes.UPDATE_OFFER_FEES_CATALOG_FAILED,
            payload: { documentId, siteId, fees, error: event }
          });
        }
      })
      .catch(error => {
        dispatch({ type: actionTypes.UPDATE_OFFER_FEES_CATALOG_FAILED, payload: { documentId, siteId, fees, error } });
        dispatch(showError(error.message, error.correlationId));
      });
  };
}

export function getOfferFeeCatalogNegotiationData(documentId, siteId) {
  return async (dispatch, getState) => {
    const { feeCatalogLoaded } = getState().currentOffer.feesData;
    if (feeCatalogLoaded) {
      return;
    }
    dispatch({ type: actionTypes.LOAD_OFFER_FEES_NEGOTIATION_DATA_STARTED, data: { documentId, siteId } });
    dispatch(
      sendHttpCommandAndWaitForEvent(
        api.getOfferFeeCatalogNegotiationData(documentId, siteId),
        "GetOfferFeeCatalogNegotiationDataInProgress",
        [eventTypes.OFFER_FINANCIAL_FEES_CATALOG_NEGOTIATION_DATA_RETRIEVED],
        false
      )
    )
      .then(event => {
        if (event.ok) {
          dispatch({
            type: actionTypes.LOAD_OFFER_FEES_NEGOTIATION_DATA_SUCCEEDED,
            payload: {
              schemaIsEligibleForNegotiation: event.eventBody.schemaIsEligibleForNegotiation,
              negotiationStatus: event.eventBody.negotiationStatus,
              negotiationType: event.eventBody.negotiationType
            }
          });
        } else {
          dispatch({
            type: actionTypes.LOAD_OFFER_FEES_NEGOTIATION_DATA_FAILED,
            payload: {
              schemaIsEligibleForNegotiation: false,
              negotiationStatus: null,
              negotiationType: null,
              error: event
            }
          });
        }
      })
      .catch(error => {
        dispatch({
          type: actionTypes.LOAD_OFFER_FEES_NEGOTIATION_DATA_FAILED,
          payload: { schemaIsEligibleForNegotiation: false, negotiationStatus: null, negotiationType: null, error }
        });
        dispatch(showError(error.message, error.correlationId));
      });
  };
}

export function startNegotiation(documentId, siteId, negotiationType) {
  return async dispatch => {
    dispatch({
      type: actionTypes.UPDATE_OFFER_FEES_START_NEGOTIATION_STARTED,
      payload: { documentId, siteId, negotiationType }
    });
    dispatch(
      sendHttpCommandAndWaitForEvent(api.startNegotiation(documentId, siteId, negotiationType), "StartNegotiation", [
        eventTypes.OFFER_FEES_INVALIDATED
      ])
    ).catch(error => {
      dispatch({
        type: actionTypes.UPDATE_OFFER_FEE_START_NEGOTIATION_FAILED,
        payload: { documentId, siteId, negotiationType, error: error }
      });
      dispatch(showError(error.message, error.correlationId));
    });
  };
}

export function sendNegotiationForApproval(documentId, siteId, fees) {
  return async dispatch => {
    dispatch({ type: actionTypes.UPDATE_OFFER_FEES_SEND_IN_APPROVAL_STARTED, payload: { documentId, siteId, fees } });
    dispatch(
      sendHttpCommandAndWaitForEvent(
        api.sendNegotiationForApproval(documentId, siteId, fees),
        "SendNegotiationForApproval",
        [eventTypes.OFFER_FEES_INVALIDATED]
      )
    ).catch(error => {
      dispatch({
        type: actionTypes.UPDATE_OFFER_FEE_SEND_IN_APPROVAL_FAILED,
        payload: { documentId, siteId, error: error }
      });
      dispatch(showError(error.message, error.correlationId));
    });
  };
}

export function confirmNegotiation(documentId, siteId) {
  return async dispatch => {
    dispatch({ type: actionTypes.UPDATE_OFFER_FEES_CONFIRM_NEGOTIATION_STARTED, payload: { documentId, siteId } });
    dispatch(
      sendHttpCommandAndWaitForEvent(api.confirmNegotiation(documentId, siteId), "ConfirmNegotiation", [
        eventTypes.OFFER_FEES_INVALIDATED
      ])
    ).catch(error => {
      dispatch({
        type: actionTypes.UPDATE_OFFER_FEE_CONFIRM_NEGOTIATION_FAILED,
        payload: { documentId, siteId, error: error }
      });
      dispatch(showError(error.message, error.correlationId));
    });
  };
}

export function confirmPartnerNegotiation(documentId, siteId) {
  return async dispatch => {
    dispatch({
      type: actionTypes.UPDATE_OFFER_FEES_CONFIRM_PARTNER_NEGOTIATION_STARTED,
      payload: { documentId, siteId }
    });
    dispatch(
      sendHttpCommandAndWaitForEvent(api.confirmPartnerNegotiation(documentId, siteId), "ConfirmPartnerNegotiation", [
        eventTypes.OFFER_FEES_INVALIDATED
      ])
    ).catch(error => {
      dispatch({
        type: actionTypes.UPDATE_OFFER_FEE_CONFIRM_PARTNER_NEGOTIATION_FAILED,
        payload: { documentId, siteId, error: error }
      });
      dispatch(showError(error.message, error.correlationId));
    });
  };
}

export function confirmPartnerGroupNegotiation(documentId, siteId) {
  return async dispatch => {
    dispatch({
      type: actionTypes.UPDATE_OFFER_FEES_CONFIRM_PARTNER_GROUP_NEGOTIATION_STARTED,
      payload: { documentId, siteId }
    });
    dispatch(
      sendHttpCommandAndWaitForEvent(
        api.confirmPartnerGroupNegotiation(documentId, siteId),
        "ConfirmPartnerGroupNegotiation",
        [eventTypes.OFFER_FEES_INVALIDATED]
      )
    ).catch(error => {
      dispatch({
        type: actionTypes.UPDATE_OFFER_FEE_CONFIRM_PARTNER_GROUP_NEGOTIATION_FAILED,
        payload: { documentId, siteId, error: error }
      });
      dispatch(showError(error.message, error.correlationId));
    });
  };
}

export function changeEditableProperty(value) {
  return dispatch => {
    dispatch({ type: actionTypes.CHANGE_EDITABLE_PROPERTY, payload: { value } });
  };
}

export function cancelFeesChanges() {
  return dispatch => {
    dispatch({ type: actionTypes.CANCEL_OFFER_FEES_CHANGES });
  };
}

export function updateOfferFeeApproval1(documentId, siteId, schemaDetailId, didApprove) {
  return async dispatch => {
    dispatch({ type: actionTypes.UPDATE_OFFER_FEE_APPROVAL1_STARTED, payload: { documentId, siteId, schemaDetailId } });
    dispatch(
      sendHttpCommandAndWaitForEvent(
        api.updateOfferFeeApproval1(documentId, siteId, schemaDetailId, didApprove),
        "UpdateOfferFeeApproval1Command",
        [eventTypes.OFFER_FEE_APPROVAL1_UPDATED]
      )
    )
      .then(event => {
        if (event.ok) {
          dispatch({ type: actionTypes.UPDATE_OFFER_FEE_APPROVAL1_SUCCEDED, payload: event.eventBody });
        } else {
          dispatch({
            type: actionTypes.UPDATE_OFFER_FEE_APPROVAL1_FAILED,
            payload: { documentId, siteId, schemaDetailId, error: event }
          });
        }
      })
      .catch(error => {
        dispatch({
          type: actionTypes.UPDATE_OFFER_FEE_APPROVAL1_FAILED,
          payload: { documentId, siteId, schemaDetailId, error: error }
        });
        dispatch(showError(error.message, error.correlationId));
      });
  };
}

export function updateOfferFeeApproval2(documentId, siteId, schemaDetailId, didApprove) {
  return async dispatch => {
    dispatch({ type: actionTypes.UPDATE_OFFER_FEE_APPROVAL2_STARTED, payload: { documentId, siteId, schemaDetailId } });
    dispatch(
      sendHttpCommandAndWaitForEvent(
        api.updateOfferFeeApproval2(documentId, siteId, schemaDetailId, didApprove),
        "UpdateOfferFeeApproval1Command",
        [eventTypes.OFFER_FEE_APPROVAL2_UPDATED]
      )
    )
      .then(event => {
        if (event.ok) {
          dispatch({ type: actionTypes.UPDATE_OFFER_FEE_APPROVAL2_SUCCEDED, payload: event.eventBody });
        } else {
          dispatch({
            type: actionTypes.UPDATE_OFFER_FEE_APPROVAL2_FAILED,
            payload: { documentId, siteId, schemaDetailId, error: event }
          });
        }
      })
      .catch(error => {
        dispatch({
          type: actionTypes.UPDATE_OFFER_FEE_APPROVAL2_FAILED,
          payload: { documentId, siteId, schemaDetailId, error: error }
        });
        dispatch(showError(error.message, error.correlationId));
      });
  };
}
